'use client';

import { ChevronLeft, ChevronRight, ExitFullscreen, Fullscreen, Loader, ZoomIn, ZoomOut } from '@bw/elements/icons';
import type { PDFDocumentProxy } from 'pdfjs-dist';
import { FC, useEffect, useRef, useState } from 'react';
import styles from './PdfViewer.module.scss';

const BASE_SCALE = 1.5;
const FIT_ZOOM = 1;
const MIN_ZOOM = 0.2;
const MAX_ZOOM = 3;
const ZOOM_STEP = 0.20;
const QUALITY_MARGIN = 1.2;

const docCache = new Map<string, PDFDocumentProxy>();

// Cap the fit width as a fraction of the screen on larger viewports
const WIDTH_BREAKPOINTS: [minScreenWidth: number, maxWidthRatio: number][] = [
	[1920, 0.45],
	[1440, 0.55],
	[1024, 0.7],
	[768, 0.85],
];

function getResponsiveMaxWidth(): number {
	const screenWidth = window.innerWidth;
	for (const [minScreenWidth, ratio] of WIDTH_BREAKPOINTS) {
		if (screenWidth >= minScreenWidth) return screenWidth * ratio;
	}
	return screenWidth;
}

function getFitWidth(canvas: HTMLCanvasElement): number {
	const slide = canvas.parentElement;
	if (!slide) return 0;
	const style = getComputedStyle(slide);
	const paddingX = parseFloat(style.paddingLeft) + parseFloat(style.paddingRight);
	const containerWidth = slide.clientWidth - paddingX;
	return Math.min(containerWidth, getResponsiveMaxWidth());
}

function sizeCanvas(canvas: HTMLCanvasElement, zoom: number) {
	if (!canvas.width) return;
	const fitWidth = getFitWidth(canvas);
	const aspect = canvas.height / canvas.width;
	canvas.style.width = `${fitWidth * zoom}px`;
	canvas.style.height = `${fitWidth * aspect * zoom}px`;
}

function needsHqUpgrade(canvas: HTMLCanvasElement, zoom: number, outputScale: number): boolean {
	const displayedPhysicalWidth = getFitWidth(canvas) * zoom * outputScale;
	return displayedPhysicalWidth * QUALITY_MARGIN > canvas.width;
}

// Render the page at a higher resolution than the initial render
async function upgradeCanvasToHq(pdf: PDFDocumentProxy, pageNum: number, canvas: HTMLCanvasElement, outputScale: number) {
	const ctx = canvas.getContext('2d');
	if (!ctx) return;

	const page = await pdf.getPage(pageNum);
	const hqViewport = page.getViewport({ scale: BASE_SCALE * outputScale * MAX_ZOOM });

	canvas.width = hqViewport.width;
	canvas.height = hqViewport.height;
	await page.render({ canvasContext: ctx, viewport: hqViewport, canvas }).promise;
}

interface PdfViewerProps {
	url: string;
	title: string;
	fallbackHref: string;
	className?: string;
}


export const PdfViewer: FC<PdfViewerProps> = ({ url, title, fallbackHref, className }) => {
	const wrapperRef = useRef<HTMLDivElement>(null);
	const trackRef = useRef<HTMLDivElement>(null);
	const [prevUrl, setPrevUrl] = useState(url);
	const [status, setStatus] = useState<'loading' | 'done' | 'error'>('loading');
	const [isFullscreen, setIsFullscreen] = useState(false);
	const [zoom, setZoom] = useState(FIT_ZOOM);
	const [numPages, setNumPages] = useState(0);
	const [currentPage, setCurrentPage] = useState(0);
	const pdfRef = useRef<PDFDocumentProxy | null>(null);
	const pageRenderRef = useRef<Map<number, Promise<void>>>(new Map());
	const hqPagesRef = useRef<Set<number>>(new Set());
	const zoomRef = useRef(zoom);
	zoomRef.current = zoom;
	const currentPageRef = useRef(currentPage);
	currentPageRef.current = currentPage;

	if (prevUrl !== url) {
		setPrevUrl(url);
		setStatus('loading');
		setCurrentPage(0);
		setNumPages(0);
	}

	useEffect(() => {
		const onChange = () => setIsFullscreen(!!document.fullscreenElement);
		document.addEventListener('fullscreenchange', onChange);
		return () => document.removeEventListener('fullscreenchange', onChange);
	}, []);

	useEffect(() => {
		let cancelled = false;
		const track = trackRef.current;
		if (!track) return;

		track.innerHTML = '';

		const load = async () => {
			const pdfjsLib = await import('pdfjs-dist');
			pdfjsLib.GlobalWorkerOptions.workerSrc = new URL(
				'pdfjs-dist/build/pdf.worker.min.mjs',
				import.meta.url
			).toString();

			let pdf = docCache.get(url);
			if (!pdf) {
				pdf = await pdfjsLib.getDocument({ url }).promise;
				docCache.set(url, pdf);
			}
			if (cancelled) return;
			pdfRef.current = pdf;
			pageRenderRef.current = new Map();
			hqPagesRef.current = new Set();
			setNumPages(pdf.numPages);

			const slides: HTMLDivElement[] = [];
			for (let i = 0; i < pdf.numPages; i++) {
				const slide = document.createElement('div');
				slide.className = styles.slide;
				track.appendChild(slide);
				slides.push(slide);
			}

			const outputScale = window.devicePixelRatio || 1;
			const renderScale = BASE_SCALE * outputScale;

			for (let pageNum = 1; pageNum <= pdf.numPages; pageNum++) {
				if (cancelled) return;

				const page = await pdf.getPage(pageNum);
				const renderViewport = page.getViewport({ scale: renderScale });

				const canvas = document.createElement('canvas');
				canvas.width = renderViewport.width;
				canvas.height = renderViewport.height;
				canvas.className = styles.page;
				slides[pageNum - 1].appendChild(canvas);
				sizeCanvas(canvas, zoomRef.current);

				const ctx = canvas.getContext('2d');
				if (!ctx) continue;

				const renderTask = page.render({ canvasContext: ctx, viewport: renderViewport, canvas }).promise;
				pageRenderRef.current.set(pageNum, renderTask);
				await renderTask;

				if (!cancelled && pageNum === 1) setStatus('done');

				// Check if the canvas needs to be upgraded to HQ immediately after rendering
				if (!cancelled && pageNum - 1 === currentPageRef.current && needsHqUpgrade(canvas, zoomRef.current, outputScale)) {
					await upgradeCanvasToHq(pdf, pageNum, canvas, outputScale);
					if (!cancelled) hqPagesRef.current.add(pageNum);
				}
			}
		};

		load().catch(() => {
			if (!cancelled) setStatus('error');
		});

		return () => {
			cancelled = true;
		};
	}, [url]);

	// Update canvas size when zoom level or container size changes
	useEffect(() => {
		const track = trackRef.current;
		if (!track) return;

		const applySizing = () => {
			const canvases = track.querySelectorAll<HTMLCanvasElement>(`.${styles.page}`);
			canvases.forEach((canvas) => sizeCanvas(canvas, zoom));
		};

		applySizing();

		const observer = new ResizeObserver(applySizing);
		observer.observe(track);
		window.addEventListener('resize', applySizing);
		return () => {
			observer.disconnect();
			window.removeEventListener('resize', applySizing);
		};
	}, [zoom, numPages]);

	// Rerender PDF at higher resolution if zoom level exceeds native PDF resolution
	useEffect(() => {
		const pdf = pdfRef.current;
		const track = trackRef.current;
		if (!pdf || !track) return;

		const pageNum = currentPage + 1;
		if (hqPagesRef.current.has(pageNum)) return;

		const canvas = track.querySelectorAll<HTMLCanvasElement>(`.${styles.page}`)[currentPage];
		if (!canvas) return;

		const outputScale = window.devicePixelRatio || 1;

		// Skip re-render only while the raster still has comfortable headroom
		if (!needsHqUpgrade(canvas, zoom, outputScale)) return;

		let cancelled = false;
		const upgrade = async () => {
			await pageRenderRef.current.get(pageNum);
			if (cancelled) return;

			await upgradeCanvasToHq(pdf, pageNum, canvas, outputScale);
			if (!cancelled) hqPagesRef.current.add(pageNum);
		};

		upgrade();
		return () => {
			cancelled = true;
		};
	}, [zoom, currentPage]);

	const toggleFullscreen = () => {
		if (!document.fullscreenElement) {
			wrapperRef.current?.requestFullscreen();
		} else {
			document.exitFullscreen();
		}
	};

	const goPrev = () => setCurrentPage((p) => Math.max(0, p - 1));
	const goNext = () =>
		setCurrentPage((p) => Math.min(Math.max(0, numPages - 1), p + 1));

	useEffect(() => {
		const onKey = (e: KeyboardEvent) => {
			if (e.key === 'ArrowLeft') goPrev();
			else if (e.key === 'ArrowRight') goNext();
			else if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
				const slide = trackRef.current?.children[currentPage] as HTMLElement | undefined;
				if (slide) {
					e.preventDefault();
					slide.scrollBy({ top: e.key === 'ArrowDown' ? 120 : -120, behavior: 'smooth' });
				}
			}
		};
		document.addEventListener('keydown', onKey);
		return () => document.removeEventListener('keydown', onKey);
	}, [numPages, currentPage]);

	return (
		<div ref={wrapperRef} className={`${styles.wrapper}${zoom !== FIT_ZOOM ? ` ${styles.zoomed}` : ''}${className ? ` ${className}` : ''}`} aria-label={title}>
			<div className={styles.toolbar}>
				{title && <span className={styles.toolbarTitle}>{title}</span>}
				<div className={styles.toolbarControls}>
					{numPages > 0 && (
						<span className={styles.pageLabel}>
							{currentPage + 1} / {numPages}
						</span>
					)}
					<div className={styles.zoomControls}>
						<button
							className={styles.toolbarBtn}
							onClick={() => setZoom((z) => Math.max(MIN_ZOOM, z - ZOOM_STEP))}
							disabled={zoom <= MIN_ZOOM}
							aria-label="Zoom out"
							title="Zoom out"
						>
							<ZoomOut width={18} height={18} aria-hidden="true" />
						</button>
						<button
							className={styles.toolbarBtn}
							onClick={() => setZoom((z) => Math.min(MAX_ZOOM, z + ZOOM_STEP))}
							disabled={zoom >= MAX_ZOOM}
							aria-label="Zoom in"
							title="Zoom in"
						>
							<ZoomIn width={18} height={18} aria-hidden="true" />
						</button>
					</div>
					<button
						className={styles.toolbarBtn}
						onClick={toggleFullscreen}
						aria-label={isFullscreen ? 'Exit fullscreen' : 'View fullscreen'}
						title={isFullscreen ? 'Exit fullscreen' : 'View fullscreen'}
					>
						{isFullscreen
							? <ExitFullscreen width={18} height={18} aria-hidden="true" />
							: <Fullscreen width={18} height={18} aria-hidden="true" />}
					</button>
				</div>
			</div>
			{status === 'loading' && (
				<div className={styles.spinnerWrapper}>
					<Loader width={48} height={48} aria-label="Loading" />
				</div>
			)}
			{status === 'error' && (
				<p className={styles.message}>
					Failed to load PDF.{' '}
					<a href={fallbackHref} target="_blank" rel="noopener noreferrer">
						Download instead ↗
					</a>
				</p>
			)}
			<div
				className={styles.viewport}
				onClick={(e) => {
					// Change page when clicking left/right areas, not only on buttons
					const { left, width } = (e.currentTarget as HTMLDivElement).getBoundingClientRect();
					const x = e.clientX - left;
					const zone = width * 0.25;
					if (x < zone) goPrev();
					else if (x > width - zone) goNext();
				}}
			>
				<button
					className={`${styles.navBtn} ${styles.navPrev}`}
					onClick={(e) => { e.stopPropagation(); goPrev(); }}
					disabled={currentPage <= 0}
					aria-label="Previous page"
					title="Previous page"
				>
					<ChevronLeft width={24} height={24} aria-hidden="true" />
				</button>
				<div
					ref={trackRef}
					className={styles.track}
					style={{ transform: `translateX(-${currentPage * 100}%)` }}
				/>
				<button
					className={`${styles.navBtn} ${styles.navNext}`}
					onClick={(e) => { e.stopPropagation(); goNext(); }}
					disabled={currentPage >= numPages - 1}
					aria-label="Next page"
					title="Next page"
				>
					<ChevronRight width={24} height={24} aria-hidden="true" />
				</button>
			</div>
		</div>
	);
};
